import React, { useState, useEffect } from 'react';
import './Profile.css';
import { User, Award } from 'lucide-react';
import { apiFetch } from '../../utils/api';
import fallback from '../../images/fallback.png';

function Profile() {
  const [user, setUser] = useState({ name: '', email: '', department: '', jobTitle: '' });
  const [certifications, setCertifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profilePicUrl, setProfilePicUrl] = useState(fallback);

  useEffect(() => {
    const fetchUser = async () => {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        setError('No userEmail found. Please log in.');
        setIsLoading(false);
        return;
      }

      try {
        // Use localStorage for basic user info
        setUser({
          name: localStorage.getItem('name') || 'N/A',
          email: userEmail || 'N/A',
          department: localStorage.getItem('department') || 'N/A',
          jobTitle: localStorage.getItem('jobTitle') || 'N/A',
        });
      } catch (error) {
        console.error('Failed to set user data from localStorage:', error);
        setError('Failed to set user data');
        setIsLoading(false);
        return;
      }

      let data;
      try {
        data = await apiFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
        if (!Number.isInteger(data.user_id)) {
          throw new Error('Invalid user_id received');
        }
      } catch (apiError) {
        console.error('API error fetching user info:', apiError);
        setError(`Failed to fetch user info: ${apiError.message || apiError}`);
        setIsLoading(false);
        return;
      }

      try {
        const certData = await apiFetch(`${window._env_.API_BASE_URL}/certificates/user/${data.user_id}`);
        setCertifications(certData.map(cert => cert.course_name));
      } catch (certError) {
        setCertifications([]);
      }

      // Fetch profile picture directly from Azure AD via backend proxy
      // This ensures we always have the latest Azure AD photo without DB storage
      const photoUrl = `${window._env_.API_BASE_URL}/profileimg/get-azure-ad-photo?email=${userEmail}`;
      setProfilePicUrl(photoUrl);

      setIsLoading(false);
    };

    fetchUser();
  }, []);

  const handleImageError = (e) => {
    e.target.src = fallback;
  };

  if (isLoading) {
    return <div className="profile-container">Loading...</div>;
  }

  if (error) {
    return <div className="profile-container error">{error}</div>;
  }

  return (
    <div className="profile-container">
      <div className="profile-header">Profile</div>
      <div className="profile-card">
        <div className="profile-avatar-section">
          <div className="profile-avatar-wrapper">
            <img
              src={profilePicUrl}
              alt="Profile"
              className="profile-avatar"
              onError={handleImageError}
            />
          </div>
        </div>
        <div className="profile-info-section">
          <div className="profile-info-block">
            <div className="profile-info-title">
              <User size={18} style={{ marginRight: 6 }} /> Personal Information
            </div>
            <div className="profile-info-row">
              <span className="profile-label">Full Name:</span> <span className="profile-value">{user.name}</span>
            </div>
            <div className="profile-info-row">
              <span className="profile-label">Email:</span> <span className="profile-value">{user.email}</span>
            </div>
            <div className="profile-info-row">
              <span className="profile-label">Department:</span> <span className="profile-value">{user.department}</span>
            </div>
            <div className="profile-info-row">
              <span className="profile-label">Job Title:</span> <span className="profile-value">{user.jobTitle}</span>
            </div>
            <div className="profile-cert-title">
              <Award size={18} style={{ marginRight: 6 }} /> Certifications
            </div>
            <div className="profile-cert-list">
              {certifications.length > 0 ? (
                certifications.map((cert) => (
                  <span className="profile-cert-badge" key={cert}>
                    {cert}
                  </span>
                ))
              ) : (
                <span className="profile-cert-badge">No certifications</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;